import type { PersistedState } from '@/types/persistence';
import { STORAGE_KEY, STORAGE_VERSION } from '@/types/persistence';

/**
 * Storage adapter interface (P1.4.1). Backed by localStorage in production;
 * swappable for an in-memory adapter in tests.
 */
export interface StorageAdapter {
  load(): string | null;
  save(data: string): void;
  clear(): void;
}

export const localStorageAdapter: StorageAdapter = {
  load(): string | null {
    try {
      return window.localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  },
  save(data: string): void {
    try {
      window.localStorage.setItem(STORAGE_KEY, data);
    } catch {
      // Quota exceeded is surfaced separately by the quota guard.
    }
  },
  clear(): void {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  },
};

/** In-memory adapter (for tests / non-browser environments). */
export function createMemoryStorage(): StorageAdapter {
  let store: string | null = null;
  return {
    load: () => store,
    save: (data: string) => {
      store = data;
    },
    clear: () => {
      store = null;
    },
  };
}

/**
 * Deserialize and migrate persisted state (P1.4.3).
 * Unknown versions fall back to defaults and back up old state.
 */
export function deserializeState(
  raw: string | null,
  defaults: PersistedState,
): PersistedState {
  if (!raw) return defaults;

  try {
    const parsed = JSON.parse(raw) as PersistedState & { version?: number };
    if (parsed.version === STORAGE_VERSION) {
      return parsed as PersistedState;
    }
    // Unknown version — back up old state and fall back to defaults.
    try {
      window.localStorage.setItem(`${STORAGE_KEY}.backup`, raw);
    } catch {
      // ignore backup failure
    }
    return defaults;
  } catch {
    return defaults;
  }
}
